/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { SortOrders } from '@confluence/template-utils/entry-points/SortByDropdown';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useNewUserTemplates } from '@confluence/experiment-land-users-in-editor-with-revamped-bpqa/entry-points/useNewUserTemplates';

import type {
	useSuggestedTemplatesSuggestedTemplatesQuery_allTemplates_PaginatedTemplateInfoList_nodes_TemplateInfo as Template,
	useSuggestedTemplatesSuggestedTemplatesQuery as useSuggestedTemplatesSuggestedTemplatesQuery$data,
	useSuggestedTemplatesSuggestedTemplatesQueryVariables as useSuggestedTemplatesSuggestedTemplatesQuery$variables,
} from './__types__/useSuggestedTemplatesSuggestedTemplatesQuery';
import { BPQA_EXCLUDED_TEMPLATE_KEYS, TEMPLATE_LIMIT } from './bpqaConst';

type SuggestedTemplatesProps = {
	spaceKey: string;
};

export const useSuggestedTemplates = ({ spaceKey }: SuggestedTemplatesProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		templates: newUserTemplates,
		error: newUserTemplatesError,
		loading: newUserTemplatesLoading,
	} = useNewUserTemplates();
	const { data, error, loading } = useQuery<
		useSuggestedTemplatesSuggestedTemplatesQuery$data,
		useSuggestedTemplatesSuggestedTemplatesQuery$variables
	>(
		gql`
			query useSuggestedTemplatesSuggestedTemplatesQuery(
				$spaceKey: String!
				$sortingScheme: String
				$limit: Int!
			) {
				allTemplates(spaceKey: $spaceKey, sortingScheme: $sortingScheme, limit: $limit) {
					nodes {
						templateId
						contentBlueprintId
						blueprintModuleCompleteKey
						itemModuleCompleteKey
						templateType
						name
						description
						iconURL
						darkModeIconURL
						styleClass
						hasWizard
						isNew
						isPromoted
						isFavourite
						hasGlobalBlueprintContent
						categoryIds
						keywords
						recommendationRank
						author
					}
				}
			}
		`,
		{
			variables: {
				spaceKey,
				sortingScheme: SortOrders.RELEVANCY_SORT,
				limit: TEMPLATE_LIMIT,
			},
			skip: !spaceKey,
		},
	);
	if (error || newUserTemplatesError) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
		if (isUnauthorizedError(newUserTemplatesError)) {
			markErrorAsHandled(newUserTemplatesError);
		}
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'BlankPageQuickActions',
				action: 'error',
				actionSubject: 'useSuggestedTemplates',
				attributes: {
					version: '2',
					revampedBpqaError: Boolean(newUserTemplatesError),
				},
			},
		}).fire();
	}

	if (loading || newUserTemplatesLoading) {
		return {
			templates: [],
			loading: true,
			error: error || newUserTemplatesError,
		};
	}

	if (newUserTemplates) {
		return {
			templates: newUserTemplates,
			loading: false,
			error: error || newUserTemplatesError,
			isNewUserTemplates: true,
		};
	}

	const suggestedTemplates = (data?.allTemplates?.nodes as Template[]) || [];
	// we want to filter out the blog post template and legacy
	// editor template, so we get 5 templates and return the
	// first 3 excluding them if they exist
	const templates = suggestedTemplates
		?.filter(
			(template) =>
				template.itemModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.BLOG_POST &&
				template.blueprintModuleCompleteKey !== BPQA_EXCLUDED_TEMPLATE_KEYS.LEGACY_EDITOR,
		)
		?.slice(0, 3);
	return { error: error || newUserTemplatesError, templates, loading };
};
